/**
 * Hover steps for weight-ruled rows — tells the client in plain words why this
 * number is what it is: which rule, what the shared total was, this row's
 * slice of it, and whether anything had to be adjusted to fit 100%.
 * Delegates to the normal optimal-weight story for untouched rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { PreviewRow } from '../../../preview-row.types';
import type { AllocatorBounds, CalcStep } from './score-calculation';
import {
  buildFreeRowWithRulesSteps,
  poolWeightRuleTotals,
} from './free-row-with-rules-steps';
import { buildOptimalWeightSteps } from './optimal-weight-steps';

const pct = (perMille: number): string => `${(perMille / 10).toFixed(1)}%`;

export function buildWeightAwareSteps(
  row: PreviewRow,
  siblings: PreviewRow[],
  bounds: AllocatorBounds,
): CalcStep[] {
  const rule = row.weightRule;
  if (!rule) {
    // Untouched row — but a pool shaped by pins/boosts needs the true story
    // (the normal one would ignore the budget the rules reserved).
    const rules = poolWeightRuleTotals(row, siblings);
    if (rules.pinnedPerMille > 0 || rules.boostAppliedPerMille > 0) {
      return buildFreeRowWithRulesSteps(row, siblings, bounds, rules);
    }
    return buildOptimalWeightSteps(row, siblings, bounds);
  }
  return rule.mode === 'static'
    ? staticSteps(row, siblings)
    : dynamicSteps(row, siblings);
}

const rowKey = (r: PreviewRow): string =>
  `${r.layer}|${r.itemId}|${r.pathId ?? ''}`;

function ruleMates(row: PreviewRow, siblings: PreviewRow[]): number {
  const key = row.weightRule?.ruleKey;
  // Identity by key, not reference — the hovered row is often a COPY of the
  // sibling-list entry, and counting yourself inflates "shared with N".
  return siblings.filter(
    (s) => rowKey(s) !== rowKey(row) && s.weightRule?.ruleKey === key,
  ).length;
}

/** `“Name”` when the rule is named; unnamed rules read as plain prose. */
function ruleLabel(rule: NonNullable<PreviewRow['weightRule']>): string {
  return rule.ruleName ? `rule “${rule.ruleName}”` : 'this weight rule';
}

const shareWord = (mates: number): string =>
  mates > 0
    ? `shared with ${mates} other match${mates > 1 ? 'es' : ''}, split by score — your slice:`
    : 'you are its only match, so you take all of it:';

function staticSteps(row: PreviewRow, siblings: PreviewRow[]): CalcStep[] {
  const rule = row.weightRule!;
  const mates = ruleMates(row, siblings);
  const step: CalcStep = {
    label: 'Pinned by a weight rule',
    formula:
      `${ruleLabel(rule)} pins its matches to ` +
      `${rule.percent}% of the pool in total (${shareWord(mates)})`,
    result: pct(row.optimalPerMille),
  };
  if (rule.adjusted) {
    step.details = [
      'Every element in this pool is pinned, so the pins were scaled ' +
        'proportionally to fill exactly 100%.',
    ];
  }
  return [step];
}

function dynamicSteps(row: PreviewRow, siblings: PreviewRow[]): CalcStep[] {
  const rule = row.weightRule!;
  const applied = rule.appliedPerMille ?? 0;
  const requested = rule.requestedPerMille ?? applied;
  const base = Math.max(0, row.optimalPerMille - applied);
  const mates = ruleMates(row, siblings);
  const boost: CalcStep = {
    label: 'Boost from a weight rule',
    formula:
      `${ruleLabel(rule)} gives its matches ` +
      `+${rule.percent} points in total (${shareWord(mates)})`,
    result: `+${pct(applied)}`,
  };
  if (rule.adjusted) {
    boost.details = [
      applied === 0
        ? 'The other elements had nothing left to give (they are boosted ' +
          'too, or already at the minimum) — so this boost could not be ' +
          'applied in this pool.'
        : `The others could only give up ${pct(applied)} of the requested ` +
          `+${pct(requested)} without dropping below the minimum — the rest ` +
          'was trimmed automatically.',
    ];
  }
  return [
    boost,
    {
      label: 'Optimal weight',
      formula: `your computed weight ${pct(base)} + the +${pct(applied)} boost`,
      result: pct(row.optimalPerMille),
    },
  ];
}
